import type { ReactNode } from "react";
import { StatCard } from "./StatCard";
import { usd, usdSigned } from "./format";
import type { CardCalculationsResponse } from "../../utils/portfolio-risk-concentration/api/card-calculations";
import { cn } from "../../utils/common/cn";

/**
 * The External Capacity cards, from card_calculations (sql/68_) via `GET /card-calculations`. The arithmetic
 * is server-side; this only formats. The bases are fixed by the card spec, so the labels say which.
 *
 * Top row, the portfolio and its funding mix (the funding cards only on "All facilities", and always Loans & Leases like the second row):
 *
 *   Total portfolio (Net Investment)   net investment for the selection
 *   Debt funded / Equity funded        the debt facilities' share of it, and 100% minus that
 *   Remaining debt / equity capacity   each facility group's ceiling minus its net investment
 *
 * Second row, what is drawn against the ceiling. These five follow the selected FACILITY (All Facility, CAP 1 or CAP 2) and always
 * show Loans & Leases: the Loans / Leases filter moves only Total portfolio above, so All Facility + Loans, + Leases and
 * + Loans & Leases read the same here, and likewise for CAP 1 and CAP 2.
 *
 *   Committed ceiling             total_ceiling (debt + equity) of the selected facility
 *   Total outstanding (DCB)       Loans & Leases net investment of the selected facility  -> % of the committed ceiling
 *   Loans / Leases outstanding (DCB)   the same with the product replaced -- always shown, one each
 *   Remaining capacity            100% - Total outstanding %
 *
 * `null` renders as "Not set" (a ceiling expected but missing) or "Not measured" (e.g. no DCB yet) -- never 0.
 */
const NOT_MEASURED = <span className="text-muted-foreground">Not measured</span>;
const NOT_SET = <span className="text-muted-foreground">Not set</span>;

const GRID = "grid grid-cols-2 gap-3 lg:grid-cols-5";
// Every card in ONE row, for a selection whose top row would hold the Total portfolio card alone (see the layout note in the component).
const GRID_ONE_ROW = "grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6";

const pct = (v: number | null) => (v === null ? null : `${v.toFixed(1)}%`);

export function CardCalculationCards({
  data,
  facilityLabel,
  className,
}: {
  data: CardCalculationsResponse;
  /** Book name as the page spells it, e.g. "CAP 1 Back Book". Only for a single facility. */
  facilityLabel?: string;
  className?: string;
}) {
  if (!data.available || !data.cards) {
    return (
      <p className={cn("text-xs text-muted-foreground", className)}>
        {data.note ?? "No capacity figures for this selection."}
      </p>
    );
  }

  const c = data.cards;
  const ceiling = c.committedCeiling;
  const remaining = c.remainingCapacity;
  const of = "of committed ceiling";
  const where =
    data.facility === "All Facility"
      ? `${data.investorName} · All facilities`
      : `${data.investorName} · ${facilityLabel ?? data.facility}`;

  // The five ceiling cards follow the selected facility (and always Loans & Leases).
  const overall = where;

  const outstandingCard = (key: string, label: string, dollars: number | null, share: number | null): ReactNode => (
    <StatCard
      aligned
      key={key}
      label={label}
      value={share === null ? NOT_MEASURED : pct(share)}
      sublabel={dollars === null ? undefined : `${of} · ${usd(dollars)}`}
    />
  );

  const f = c.funding;

  // Top row: the portfolio and, on "All facilities", its funding mix.
  const topCards: ReactNode[] = [
    <StatCard
      aligned
      key="total-portfolio"
      label="Total portfolio (Net Investment)"
      value={c.totalPortfolio === null ? NOT_MEASURED : usd(c.totalPortfolio)}
      sublabel={where}
    />,
  ];
  if (f) {
    topCards.push(
      <StatCard
        aligned
        key="debt-funded"
        label="Debt funded"
        value={f.debtFundedPct === null ? "—" : pct(f.debtFundedPct)}
        sublabel={`of total portfolio · ${usd(f.debtOutstanding)} in the debt facilities`}
      />,
      <StatCard
        aligned
        key="equity-funded"
        label="Equity funded"
        value={f.equityFundedPct === null ? "—" : pct(f.equityFundedPct)}
        sublabel={`of total portfolio · ${usd(f.equityOutstanding)} in the equity facility`}
      />,
      <StatCard
        aligned
        key="remaining-debt"
        label="Remaining debt capacity"
        value={f.remainingDebtCapacity === null ? NOT_SET : usdSigned(f.remainingDebtCapacity)}
        sublabel="Debt facility ceilings minus their net investment"
        accent={f.remainingDebtCapacity !== null && f.remainingDebtCapacity < 0 ? "red" : "default"}
      />,
      <StatCard
        aligned
        key="remaining-equity"
        label="Remaining equity capacity"
        value={f.remainingEquityCapacity === null ? NOT_SET : usdSigned(f.remainingEquityCapacity)}
        sublabel={
          f.equityCeiling === null
            ? "No equity ceiling in effect"
            : `Equity ceiling ${usd(f.equityCeiling)} minus its net investment`
        }
        accent={f.remainingEquityCapacity !== null && f.remainingEquityCapacity < 0 ? "red" : "default"}
      />,
    );
  }

  // Second row: what is drawn against the ceiling.
  const bottomCards: ReactNode[] = [
    <StatCard
      aligned
      key="committed-ceiling"
      label="Committed ceiling"
      value={ceiling === null ? NOT_SET : usd(ceiling)}
      sublabel={
        ceiling === null
          ? `A ceiling is expected here but missing · ${overall}`
          : `Debt + equity · ${overall}`
      }
      accent={ceiling === null ? "amber" : "default"}
    />,
    <StatCard
      aligned
      key="total-outstanding"
      label="Total outstanding (DCB)"
      value={c.totalOutstandingPct === null ? NOT_MEASURED : pct(c.totalOutstandingPct)}
      sublabel={c.totalOutstanding === null ? undefined : `${of} · ${usd(c.totalOutstanding)}`}
      accent={
        remaining !== null && remaining < 0
          ? "red"
          : c.totalOutstandingPct !== null && c.totalOutstandingPct >= 90
            ? "amber"
            : "default"
      }
    />,
    outstandingCard("loans-outstanding", "Loans outstanding (DCB)", c.loansOutstanding, c.loansOutstandingPct),
    outstandingCard("leases-outstanding", "Leases outstanding (DCB)", c.leasesOutstanding, c.leasesOutstandingPct),
    <StatCard
      aligned
      key="remaining-capacity"
      label="Remaining capacity"
      value={c.remainingCapacityPct === null ? NOT_MEASURED : pct(c.remainingCapacityPct)}
      sublabel={
        remaining === null ? undefined : `${of} ${remaining < 0 ? "over" : "unused"} · ${usdSigned(remaining)}`
      }
      accent={remaining !== null && remaining < 0 ? "red" : "default"}
    />,
  ];

  // When the top row would be the Total portfolio card on its own (a single facility: no funding mix), the second row's cards join it, so
  // the six sit in one row instead of one lonely card above five. With the funding cards the top row is already full, so two rows stay.
  const oneRow = topCards.length === 1;

  return (
    <div className={cn("space-y-3", className)}>
      {oneRow ? (
        <div className={GRID_ONE_ROW}>{[...topCards, ...bottomCards]}</div>
      ) : (
        <>
          <div className={GRID}>{topCards}</div>
          <div className={GRID}>{bottomCards}</div>
        </>
      )}

      {data.note ? <p className="text-[11px] text-amber">{data.note}</p> : null}
      <p className="text-[11px] text-muted-foreground">
        As of file date {data.asOfDate ?? "—"} · Total portfolio shown for {data.facility} · {data.product}. Committed
        ceiling, outstanding and Remaining capacity follow the selected facility and always cover Loans &amp; Leases, so
        the Loans / Leases filter does not change them. Total portfolio is net investment; every outstanding figure is DCB.
      </p>
    </div>
  );
}
